import { booleanAttribute, Component, EventEmitter, HostBinding, Input, OnChanges, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AreaSnapshot, AreaValue } from '@models/areaInstance.model';
import { DashboardService } from '@shared/services/dashboard.service';
import { StorageService } from '@services/storage.service';
import { ToastService } from '@shared/services/toast.service';

/** Áreas de un menú de un mismo tipo (plantilla): el título es su nombre, el que le puso el admin ("Parcela"). */
export type AreaNavigationNode = Pick<AreaValue, 'areaEntryId' | 'areaId' | 'parentId' | 'name'>;

interface AreaBreadcrumbGroup {
  title: string;
  items: AreaNavigationNode[];
}

/** Un nivel del camino: el área y las del mismo nivel (sus "hermanas", ella incluida), agrupadas por tipo. */
interface AreaBreadcrumbLevel {
  node:          AreaNavigationNode;
  siblings:      AreaNavigationNode[];
  siblingGroups: AreaBreadcrumbGroup[];
}

/**
 * Migas de pan de un área ("Áreas › Finca Principal ▾ › Parcela A ▾ › ▾"): el camino hasta el área, y en cada nivel
 * un desplegable con las áreas de ese mismo nivel (un área puede tener varias subáreas) y, al final, sus subáreas.
 * En el dashboard cada área lleva a su dashboard; en la ficha, a su ficha.
 */
@Component({
  standalone: false,
  selector: 'app-area-breadcrumb',
  templateUrl: './area-breadcrumb.component.html',
  styleUrls: ['./area-breadcrumb.component.scss'],
})
export class AreaBreadcrumbComponent implements OnChanges {
  /** Árbol de la instancia (`templateValues`). */
  @Input() nodes: AreaNavigationNode[] = [];
  /** Plantillas de la instancia (`templateSnapshot`): el nombre de cada tipo de área, título de los menús. */
  @Input() templates: AreaSnapshot[] = [];
  @Input() areaInstanceId = '';
  /** Área de la pantalla. */
  @Input() areaEntryId = '';
  /** A dónde lleva cada área: su dashboard o su ficha. */
  @Input() target: 'dashboard' | 'edit' | 'external' = 'dashboard';
  @Input() listRoute = '/intranet/areas';
  @Output() areaSelected = new EventEmitter<AreaNavigationNode>();
  /** Sin margen debajo (encima de un título, como en la cabecera del dashboard). */
  @HostBinding('class.area-breadcrumb--compact') @Input({ transform: booleanAttribute }) compact = false;

  levels: AreaBreadcrumbLevel[] = [];
  children: AreaNavigationNode[] = [];
  childGroups: AreaBreadcrumbGroup[] = [];

  constructor(
    private readonly router: Router,
    private readonly dashboardService: DashboardService,
    private readonly storage: StorageService,
    private readonly toast: ToastService,
  ) {}

  ngOnChanges(): void {
    const same = (a?: string | null, b?: string | null) => !!a && !!b && a.toLowerCase() === b.toLowerCase();
    const current = this.nodes.find(node => same(node.areaEntryId, this.areaEntryId));
    const levels: AreaBreadcrumbLevel[] = [];
    const seen = new Set<string>();
    let node = current;
    while (node && !seen.has(node.areaEntryId)) {
      seen.add(node.areaEntryId);
      const parentId = node.parentId ?? null;
      const siblings = this.nodes.filter(item => parentId ? same(item.parentId, parentId) : !item.parentId);
      levels.unshift({ node, siblings, siblingGroups: this.groupByType(siblings) });
      node = parentId ? this.nodes.find(item => same(item.areaEntryId, parentId)) : undefined;
    }
    this.levels = levels;
    this.children = current ? this.nodes.filter(item => same(item.parentId, current.areaEntryId)) : [];
    this.childGroups = this.groupByType(this.children);
  }

  /** Agrupa las áreas por su tipo (plantilla), en el orden en que salen; el título, el nombre del tipo. */
  private groupByType(items: AreaNavigationNode[]): AreaBreadcrumbGroup[] {
    const names = new Map(this.templates.map(template => [template.areaId.toLowerCase(), template.name]));
    const groups = new Map<string, AreaBreadcrumbGroup>();
    for (const item of items) {
      const title = names.get((item.areaId ?? '').toLowerCase()) || 'Áreas';
      const group = groups.get(title) ?? { title, items: [] };
      group.items.push(item);
      groups.set(title, group);
    }
    return [...groups.values()];
  }

  isCurrent(node: AreaNavigationNode): boolean {
    return node.areaEntryId.toLowerCase() === this.areaEntryId.toLowerCase();
  }

  goToList(): void {
    this.router.navigateByUrl(this.listRoute);
  }

  /**
   * Abre esa área: su ficha, o su dashboard (el mismo servicio que la lista de áreas; si el área no tiene dashboard,
   * se avisa: solo existe si el admin creó uno para su plantilla).
   */
  open(node: AreaNavigationNode): void {
    if (this.isCurrent(node)) return;
    if (this.target === 'external') {
      this.areaSelected.emit(node);
      return;
    }
    if (this.target === 'edit') {
      this.router.navigate(['/intranet/areas', this.areaInstanceId, 'edit', node.areaId, node.areaEntryId]);
      return;
    }
    const userId = this.storage.get('userId') ?? '';
    this.dashboardService.getDashboardByUserAreaInstanceNode(userId, this.areaInstanceId, node.areaEntryId).subscribe({
      next: dashboard => this.router.navigate(['/intranet/areas/dashboard', dashboard.id]),
      error: () => this.toast.errorToast(`${node.name} no tiene dashboard`),
    });
  }

  trackByNode = (_: number, node: AreaNavigationNode) => node.areaEntryId;
  trackByLevel = (_: number, level: AreaBreadcrumbLevel) => level.node.areaEntryId;
}
